// src/routes/MediaSelection/MediaSelectionCard.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Button,
  Grid2 as Grid,
  Card,
  CardContent,
  Collapse,
  useTheme
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import GroupIcon from '@mui/icons-material/Group';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { gradient } from '../../utils/darkModeAdapter';

/**
 * plan: {
 *   planName: string;
 *   planSummary: string;
 *   mediaList: [
 *     {
 *       name: string;
 *       coverage: string;
 *       cost: number;
 *       expectedApplicants: number;
 *       planComment?: string;
 *       advantage?: string;
 *       disadvantage?: string;
 *       ...
 *     }
 *   ];
 * }
 * planLabel: "Aプラン" or "Bプラン" etc. (オプション)
 * isFlipped: プランが裏返しかどうか
 * onShowInquiry: お問い合わせボタンを押した時の処理
 * onToggleFlip: プラン切り替えボタンを押した時の処理
 * hasOtherPlan: 他のプランがあるかどうか
 * handleMouseEnter: マウスホバー開始時の処理
 * handleMouseLeave: マウスホバー終了時の処理
 */

// 円を「〇万」表記に変換
function convertToManEn(value) {
  if (value === null || value === undefined) return '未入力';
  const num = parseInt(value, 10);
  if (isNaN(num)) return value; // 数字として変換不可ならそのまま表示
  if (num === 0) return '要確認';
  return `${Math.floor(num / 10000)} 万円`;
}

// ラベルを確実に固定するための定数
const FIXED_LABELS = {
  MERIT: 'メリット',
  DEMERIT: 'デメリット'
};

// ラベルを安全に取得する関数（どんなデータが来ても固定ラベルを返す）
function getSafeLabel(type) {
  // データの内容に関係なく、常に固定ラベルを返す
  return type === 'merit' ? FIXED_LABELS.MERIT : FIXED_LABELS.DEMERIT;
}

// advantage/disadvantage の内容をサニタイズ
function sanitizeContent(content, type) {
  if (!content) return type === 'advantage' ? 'メリット情報なし' : 'デメリット情報なし';
  
  // 不適切なキーワードを適切な内容に置換
  const prohibitedKeywords = ['野球', '特典', 'サッカー', 'テニス', 'ゲーム'];
  let sanitized = content;
  
  for (const keyword of prohibitedKeywords) {
    if (sanitized.includes(keyword)) {
      console.log(`[MediaSelectionCard] Sanitizing inappropriate content: ${keyword}`);
      sanitized = type === 'advantage' 
        ? '専門性の高い応募者を獲得できます。'
        : '詳細な検討が必要です。';
      break;
    }
  }
  
  return sanitized;
}

export default function MediaSelectionCard({
  plan,
  planLabel,
  onShowInquiry,
  handleMouseEnter,
  handleMouseLeave
}) {
  if (!plan) {
    return (
      <Typography color="text.secondary" sx={{ fontSize: '1rem', mt: 1 }}>
        {planLabel || ''} のデータがありません。
      </Typography>
    );
  }

  // ボタンのデザイナースタイル
  const inquiryButtonStyle = {
    borderRadius: '16px',
    textTransform: 'none',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    color: '#fff',
    background: planLabel === 'Bプラン'
      ? 'linear-gradient(135deg, #FF9800 0%, #FF5722 100%)' 
      : 'linear-gradient(135deg, #66bb6a 0%, #26c6da 100%)',
    boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
    px: 3.5,
    py: 1.2,
    minWidth: '220px',
    transition: 'all 0.3s ease',
    position: 'relative',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 15px rgba(0,0,0,0.2)',
      background: planLabel === 'Bプラン'
        ? 'linear-gradient(135deg, #FB8C00 0%, #E64A19 100%)' 
        : 'linear-gradient(135deg, #43a047 0%, #00acc1 100%)',
    },
    '&:active': {
      transform: 'translateY(1px)',
      boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
    },
  };

  // MediaCard コンポーネント (各媒体を表示)
  const MediaCard = ({ media, index }) => {
    const theme = useTheme();
    const [isHovered, setIsHovered] = useState(false);
    const [showDetails, setShowDetails] = useState(false);

    const expected = media.expectedApplicants || 0;
    const costVal = media.cost || 0;
    const costPerApplicant = expected > 0 ? Math.floor(costVal / expected) : 0;

    return (
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.4,
          delay: index * 0.1,
          ease: [0.4, 0, 0.2, 1],
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{ height: '100%' }}
      >
        <Card
          component={motion.div}
          animate={{
            y: isHovered ? -8 : 0,
            transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
          }}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            boxShadow: isHovered
              ? '0 12px 40px rgba(0,0,0,0.16)'
              : '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'box-shadow 0.3s ease',
            background: gradient(theme).card,
            overflow: 'visible',
          }}
        >
          <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            {/* 媒体名 */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 1.5,
                fontSize: '1.1rem',
                color: '#212121',
              }}
            >
              {media.name || '媒体名不明'}
            </Typography>

            {/* カバー範囲 */}
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 2,
                lineHeight: 1.6,
              }}
            >
              {media.coverage || 'カバー範囲不明'}
            </Typography>

            {/* メトリクス */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
              {/* 料金 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(245, 124, 0, 0.1)',
                  }}
                >
                  <MonetizationOnIcon sx={{ fontSize: 22, color: '#f57c00' }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    料金
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#212121' }}>
                    {convertToManEn(costVal)}
                  </Typography>
                </Box>
              </Box>

              {/* 想定応募者数 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  }}
                >
                  <GroupIcon sx={{ fontSize: 22, color: '#1976d2' }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    想定応募者数
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#212121' }}>
                    {expected}人
                  </Typography>
                </Box>
              </Box>

              {/* 応募単価 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(158, 158, 158, 0.1)',
                  }}
                >
                  <MonetizationOnIcon sx={{ fontSize: 20, color: '#9e9e9e' }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    応募単価
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#212121' }}>
                    {convertToManEn(costPerApplicant)}/人
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* コメント */}
            {media.planComment && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: 'rgba(158, 158, 158, 0.05)',
                  display: 'flex',
                  gap: 1,
                  mb: 2,
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 18, color: '#9e9e9e', flexShrink: 0, mt: 0.3 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                  {media.planComment}
                </Typography>
              </Box>
            )}

            <Box sx={{ flexGrow: 1 }} />

            {/* 詳細展開ボタン */}
            <Button
              onClick={() => setShowDetails(!showDetails)}
              endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                mt: 'auto',
                textTransform: 'none',
                fontWeight: 600,
                color: planLabel === 'Bプラン' ? '#f57c00' : '#1976d2',
                '&:hover': {
                  backgroundColor:
                    planLabel === 'Bプラン'
                      ? 'rgba(245, 124, 0, 0.08)'
                      : 'rgba(25, 118, 210, 0.08)',
                },
              }}
            >
              {showDetails ? 'メリット・デメリットを閉じる' : 'メリット・デメリットを見る'}
            </Button>

            {/* 詳細情報（展開式） */}
            <AnimatePresence>
              {showDetails && (
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                  sx={{ overflow: 'hidden' }}
                >
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                    {/* メリット */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#4caf50' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4caf50' }}>
                          {getSafeLabel('merit')}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.7,
                          pl: 4,
                        }}
                      >
                        {sanitizeContent(media.advantage, 'advantage')}
                      </Typography>
                    </Box>

                    {/* デメリット */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CancelIcon sx={{ fontSize: 20, color: '#f44336' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f44336' }}>
                          {getSafeLabel('demerit')}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.7,
                          pl: 4,
                        }}
                      >
                        {sanitizeContent(media.disadvantage, 'disadvantage')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>
      </Box>
    );
  };

  // メディアカードグリッド表示
  const renderMediaGrid = () => {
    if (!Array.isArray(plan.mediaList)) return null;

    return (
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {plan.mediaList.map((media, idx) => (
          <Grid key={`${media.name}_${idx}`} size={{ xs: 12, md: 6, lg: 4 }}>
            <MediaCard media={media} index={idx} />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box>
      {/* プラン名 */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          mb: 1.5,
          color: planLabel === 'Bプラン' ? '#e65100' : '#249BE0',
        }}
      >
        {plan?.planName || `(${planLabel}名なし)`}
      </Typography>

      {/* プラン概要 */}
      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', fontSize: '1.1rem' }}>
        {plan?.planSummary || `${planLabel} の概要がありません。`}
      </Typography>

      {/* お問い合わせボタン */}
      <Box sx={{ mb: 4 }}>
        <Button
          variant="contained"
          sx={inquiryButtonStyle}
          onClick={onShowInquiry}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          このプランについて問い合わせる
        </Button>
      </Box>

      {/* メディア一覧カードグリッド */}
      {renderMediaGrid()}
    </Box>
  );
}